import {
  Permission,
  PermissionPost,
  PermissionPut,
  Role,
  RolePost,
  RolePut,
} from "callable-data-objects/dist/models/role";
import { APIRoleContext } from "Providers/APIContexts";
import React, { useEffect, useState } from "react";
import { useAPI } from "./APIProvider";
import { APIRoleProps, DefaultProviderProps } from "./types";

const APIRoleProvider = ({ children }: DefaultProviderProps) => {
  const api = useAPI();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [role, setRole] = useState<Role | undefined>();
  const [permission, setPermission] = useState<Permission | undefined>();
  const [createRole, setCreateRole] = useState<RolePost | undefined>();
  const [createPermission, setCreatePermission] =
    useState<PermissionPost | undefined>();
  const [loading, setLoading] = useState<boolean>(false);

  async function listRoles() {
    setLoading(true);
    const response = await api.roleService.list();
    setRoles(response);
    setLoading(false);
  }

  async function listPermissions() {
    setLoading(true);
    const response = await api.permissionService.list();
    setPermissions(response);
    setLoading(false);
  }

  useEffect(() => {
    listRoles();
    listPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function selectRole(id: string): void {
    setLoading(true);
    api.roleService.get(id).then((resp: Role) => {
      setRole(resp);
      setLoading(false);
    });
  }

  function selectPermission(id: string): void {
    setLoading(true);
    api.permissionService.get(id).then((resp: Permission) => {
      setPermission(resp);
      setLoading(false);
    });
  }

  function unselectRole() {
    setRole(undefined);
  }

  function unselectPermission() {
    setPermission(undefined);
  }

  function uncreateRole() {
    setCreateRole(undefined);
  }

  function uncreatePermission() {
    setCreatePermission(undefined);
  }

  function validateRoleName(value: string): string | undefined {
    let error: string | undefined;
    if (value === null || value === "") error = "Name required";
    if (value.length < 3) error = "Name must be 3 or more characters";
    if (
      roles
        .filter((rt) => role && role.id !== rt.id)
        .map((rt) => rt.name)
        .indexOf(value) > -1
    ) {
      error = "Name not unique";
    }
    return error;
  }

  function validatePermissionName(value: string): string | undefined {
    let error: string | undefined;
    if (value === null || value === "") error = "Name required";
    if (value.length < 3) error = "Name must be 3 or more characters";
    if (
      permissions
        .filter((p) => permission && permission.id !== p.id)
        .map((p) => p.name)
        .indexOf(value) > -1
    ) {
      error = "Name not unique";
    }
    return error;
  }

  async function saveRole(payload: RolePost) {
    if (createRole) {
      await api.roleService.post("", payload);
      listRoles();
      uncreateRole();
    }
  }

  async function savePermission(payload: PermissionPost) {
    if (createPermission) {
      await api.permissionService.post("", payload);
      listPermissions();
      uncreatePermission();
    }
  }

  async function updateRole(payload: RolePut) {
    if (role) {
      await api.roleService.put(payload.id, payload);
      listRoles();
      selectRole(payload.id);
    }
  }

  async function updatePermission(payload: PermissionPut) {
    if (permission) {
      await api.permissionService.put(payload.id, payload);
      listPermissions();
      selectPermission(payload.id);
    }
  }

  async function removeRole(id: string) {
    await api.roleService.delete(id);
    listRoles();
    unselectRole();
  }

  async function removePermission(id: string) {
    await api.permissionService.delete(id);
    listPermissions();
    unselectPermission();
  }

  async function assignPermission(permissionId: string) {
    if (role) {
      const response = await api.roleService.addPermission(
        role.id,
        permissionId
      );
      if (response) setRole(response);
    }
  }

  async function revokePermission(permissionId: string) {
    if (role) {
      const response = await api.roleService.removePermission(
        role.id,
        permissionId
      );
      if (response) setRole(response);
    }
  }

  const codeDeckProps: APIRoleProps = {
    role,
    roles,
    permission,
    permissions,
    createRole,
    createPermission,
    loading,
    saveRole,
    savePermission,
    updateRole,
    updatePermission,
    validateRoleName,
    validatePermissionName,
    selectRole,
    selectPermission,
    unselectRole,
    unselectPermission,
    uncreateRole,
    uncreatePermission,
    removeRole,
    removePermission,
    assignPermission,
    revokePermission,
    setCreateRole,
    setCreatePermission,
  };

  return (
    <APIRoleContext.Provider value={codeDeckProps}>
      {children}
    </APIRoleContext.Provider>
  );
};

export default APIRoleProvider;
